import type { InvoiceStatus, PaymentStatus } from '@buildbee/contracts';
import { applyBps, roundCents } from '../common';

/**
 * Owner billing math: simple invoices, progress billing and AIA G702/G703 style payment
 * applications with retainage and tax, plus payment application and invoice status.
 *
 * G703 columns: C scheduled value, D work completed from previous applications, E work
 * completed this period, F materials presently stored (not in D or E), G total completed and
 * stored to date (D + E + F), G/C percent, H balance to finish (C - G), I retainage.
 */

// ---------------------------------------------------------------------------
// Dates (calendar days, `YYYY-MM-DD`)
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

export function parseDateOnly(value: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) throw new Error(`Invalid date "${value}" (expected YYYY-MM-DD)`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}

export function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(dateOnly: string, days: number): string {
  return toDateOnly(new Date((parseDateOnly(dateOnly) + days) * DAY_MS));
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function daysBetween(from: string, to: string): number {
  return parseDateOnly(to) - parseDateOnly(from);
}

// ---------------------------------------------------------------------------
// Simple invoices
// ---------------------------------------------------------------------------

export type InvoiceLineAmounts = { quantity: number; unitPriceCents: number; taxable?: boolean };

export type InvoiceTotals = {
  lineTotalsCents: number[];
  subtotalCents: number;
  taxableCents: number;
  taxCents: number;
  retainageCents: number;
  /** Amount due: subtotal + tax - retainage withheld. */
  totalCents: number;
};

/**
 * Totals for a standard invoice. Retainage is withheld on the pre-tax subtotal; tax applies to the
 * taxable lines in full (retainage does not reduce the taxable base).
 */
export function invoiceTotals(lines: InvoiceLineAmounts[], opts: { taxRateBps?: number; retainageBps?: number } = {}): InvoiceTotals {
  const lineTotalsCents = lines.map((l) => roundCents(l.quantity * l.unitPriceCents));
  const subtotalCents = lineTotalsCents.reduce((a, b) => a + b, 0);
  const taxableCents = lines.reduce((sum, l, i) => (l.taxable === false ? sum : sum + lineTotalsCents[i]!), 0);
  const taxCents = applyBps(taxableCents, opts.taxRateBps ?? 0);
  const retainageCents = applyBps(subtotalCents, opts.retainageBps ?? 0);
  return { lineTotalsCents, subtotalCents, taxableCents, taxCents, retainageCents, totalCents: subtotalCents + taxCents - retainageCents };
}

// ---------------------------------------------------------------------------
// Schedule of values / G703
// ---------------------------------------------------------------------------

export type SovLineInput = {
  key: string;
  description: string;
  costCode?: string | null;
  /** C */
  scheduledValueCents: number;
  /** D: work completed in previous applications. */
  previousWorkCents: number;
  /** F of the previous application (materials stored at that time). */
  previousStoredCents: number;
  /** I of the previous application. */
  previousRetainageCents: number;
  /** E */
  thisPeriodWorkCents: number;
  /** F: materials presently stored (not yet installed). */
  storedMaterialsCents: number;
  taxable?: boolean;
  /** Line comes from an approved change order (G702 line 2). */
  isChangeOrder?: boolean;
};

export type G703Line = SovLineInput & {
  /** G = D + E + F */
  totalCompletedCents: number;
  /** G / C, basis points */
  percentCompleteBps: number;
  /** H = C - G */
  balanceToFinishCents: number;
  /** I (cumulative) */
  retainageCents: number;
  retainageThisPeriodCents: number;
  /** Gross billed this period: G - (previous D + previous F). */
  grossThisPeriodCents: number;
};

export type RetainageRates = {
  /** Retainage on completed work (D + E). */
  workRetainageBps: number;
  /** Retainage on stored materials (F); defaults to the work rate. */
  storedRetainageBps?: number;
};

export function computeG703Line(line: SovLineInput, rates: RetainageRates): G703Line {
  const workToDate = line.previousWorkCents + line.thisPeriodWorkCents;
  const totalCompletedCents = workToDate + line.storedMaterialsCents;
  const retainageCents =
    applyBps(workToDate, rates.workRetainageBps) + applyBps(line.storedMaterialsCents, rates.storedRetainageBps ?? rates.workRetainageBps);
  return {
    ...line,
    totalCompletedCents,
    percentCompleteBps: line.scheduledValueCents !== 0 ? Math.round((totalCompletedCents * 10_000) / line.scheduledValueCents) : 0,
    balanceToFinishCents: line.scheduledValueCents - totalCompletedCents,
    retainageCents,
    retainageThisPeriodCents: retainageCents - line.previousRetainageCents,
    grossThisPeriodCents: totalCompletedCents - (line.previousWorkCents + line.previousStoredCents),
  };
}

export function computeG703(lines: SovLineInput[], rates: RetainageRates): G703Line[] {
  return lines.map((l) => computeG703Line(l, rates));
}

/**
 * Work completed this period (E) needed to reach a cumulative percent complete (G / C), given
 * work from previous applications (D) and materials presently stored (F).
 */
export function workForPercentComplete(scheduledValueCents: number, percentCompleteBps: number, previousWorkCents: number, storedMaterialsCents = 0): number {
  return applyBps(scheduledValueCents, percentCompleteBps) - previousWorkCents - storedMaterialsCents;
}

export type SovIssue = { key: string; message: string };

/** Billing rules for a G703: nothing negative to date and no billing beyond the scheduled value. */
export function validateG703(lines: G703Line[], opts: { allowOverbilling?: boolean } = {}): SovIssue[] {
  const issues: SovIssue[] = [];
  const keys = new Set<string>();
  for (const l of lines) {
    if (keys.has(l.key)) issues.push({ key: l.key, message: 'Duplicate schedule of values line' });
    keys.add(l.key);
    if (l.storedMaterialsCents < 0) issues.push({ key: l.key, message: 'Stored materials cannot be negative' });
    if (l.previousWorkCents + l.thisPeriodWorkCents < 0) issues.push({ key: l.key, message: 'Work completed to date cannot be negative' });
    if (!opts.allowOverbilling && l.scheduledValueCents >= 0 && l.totalCompletedCents > l.scheduledValueCents) {
      issues.push({ key: l.key, message: `${l.description}: billed to date exceeds the scheduled value` });
    }
  }
  return issues;
}

export type G702Summary = {
  /** 1 */
  originalContractSumCents: number;
  /** 2 */
  netChangeByChangeOrdersCents: number;
  /** 3 = 1 + 2 */
  contractSumToDateCents: number;
  /** 4 = G total */
  totalCompletedAndStoredCents: number;
  /** 5a */
  retainageOnCompletedWorkCents: number;
  /** 5b */
  retainageOnStoredMaterialCents: number;
  /** 5 = I total */
  totalRetainageCents: number;
  /** 6 = 4 - 5 */
  totalEarnedLessRetainageCents: number;
  /** 7 */
  previousCertificatesCents: number;
  /** 8 = 6 - 7 */
  currentPaymentDueCents: number;
  /** 9 = 3 - 6 */
  balanceToFinishInclRetainageCents: number;
  grossThisPeriodCents: number;
  retainageThisPeriodCents: number;
  taxableThisPeriodCents: number;
  taxCents: number;
  /** 8 + tax */
  amountDueCents: number;
};

/**
 * G702 application summary. Previous certificates are derived from the previous application's
 * totals carried on the lines (sum of previous D + F - I) unless passed explicitly.
 */
export function computeG702(
  lines: G703Line[],
  rates: RetainageRates & { taxRateBps?: number; previousCertificatesCents?: number },
): G702Summary {
  let original = 0;
  let changes = 0;
  let total = 0;
  let retWork = 0;
  let retStored = 0;
  let previousCertificates = 0;
  let gross = 0;
  let retainageThisPeriod = 0;
  let taxable = 0;
  for (const l of lines) {
    if (l.isChangeOrder) changes += l.scheduledValueCents;
    else original += l.scheduledValueCents;
    total += l.totalCompletedCents;
    retWork += applyBps(l.previousWorkCents + l.thisPeriodWorkCents, rates.workRetainageBps);
    retStored += applyBps(l.storedMaterialsCents, rates.storedRetainageBps ?? rates.workRetainageBps);
    previousCertificates += l.previousWorkCents + l.previousStoredCents - l.previousRetainageCents;
    gross += l.grossThisPeriodCents;
    retainageThisPeriod += l.retainageThisPeriodCents;
    if (l.taxable !== false) taxable += l.grossThisPeriodCents;
  }
  const totalRetainage = retWork + retStored;
  const earned = total - totalRetainage;
  const prev = rates.previousCertificatesCents ?? previousCertificates;
  const currentDue = earned - prev;
  const taxCents = applyBps(Math.max(0, taxable), rates.taxRateBps ?? 0);
  return {
    originalContractSumCents: original,
    netChangeByChangeOrdersCents: changes,
    contractSumToDateCents: original + changes,
    totalCompletedAndStoredCents: total,
    retainageOnCompletedWorkCents: retWork,
    retainageOnStoredMaterialCents: retStored,
    totalRetainageCents: totalRetainage,
    totalEarnedLessRetainageCents: earned,
    previousCertificatesCents: prev,
    currentPaymentDueCents: currentDue,
    balanceToFinishInclRetainageCents: original + changes - earned,
    grossThisPeriodCents: gross,
    retainageThisPeriodCents: retainageThisPeriod,
    taxableThisPeriodCents: taxable,
    taxCents,
    amountDueCents: currentDue + taxCents,
  };
}

/**
 * Carry a G703 forward as the "previous" columns of the next application: D' = D + E,
 * previous F' = F, previous I' = I. This-period columns start at zero; stored materials carry
 * over until they are installed.
 */
export function carryForward(lines: G703Line[]): SovLineInput[] {
  return lines.map((l) => ({
    key: l.key,
    description: l.description,
    costCode: l.costCode ?? null,
    scheduledValueCents: l.scheduledValueCents,
    previousWorkCents: l.previousWorkCents + l.thisPeriodWorkCents,
    previousStoredCents: l.storedMaterialsCents,
    previousRetainageCents: l.retainageCents,
    thisPeriodWorkCents: 0,
    storedMaterialsCents: l.storedMaterialsCents,
    taxable: l.taxable,
    isChangeOrder: l.isChangeOrder,
  }));
}

// ---------------------------------------------------------------------------
// Payments + status
// ---------------------------------------------------------------------------

export type PaymentAmounts = { amountCents: number; refundedCents: number; status: PaymentStatus };

const COUNTED: ReadonlySet<PaymentStatus> = new Set<PaymentStatus>(['succeeded', 'partially_refunded', 'refunded']);

/** Net cash received: settled payments minus refunds (pending/failed payments do not count). */
export function amountPaid(payments: PaymentAmounts[]): number {
  return payments.reduce((sum, p) => (COUNTED.has(p.status) ? sum + p.amountCents - p.refundedCents : sum), 0);
}

export function invoiceBalance(totalCents: number, payments: PaymentAmounts[]) {
  const amountPaidCents = amountPaid(payments);
  return { amountPaidCents, balanceCents: totalCents - amountPaidCents };
}

export type InvoiceStatusInput = {
  status: InvoiceStatus;
  totalCents: number;
  amountPaidCents: number;
  dueDate: string;
  today: string;
  viewedAt?: Date | null;
};

/** Status after a payment or as time passes. Draft and void are only changed by explicit actions. */
export function deriveInvoiceStatus(i: InvoiceStatusInput): InvoiceStatus {
  if (i.status === 'void' || i.status === 'draft') return i.status;
  if (i.amountPaidCents >= i.totalCents && i.totalCents > 0) return 'paid';
  if (i.totalCents <= 0 && i.amountPaidCents >= i.totalCents) return 'paid';
  if (i.amountPaidCents > 0) return 'partially_paid';
  if (daysBetween(i.dueDate, i.today) > 0) return 'overdue';
  return i.viewedAt ? 'viewed' : 'sent';
}

export type PaymentCheck = { ok: true } | { ok: false; reason: string };

/** A payment must be positive and may not exceed the open balance. */
export function validatePaymentAmount(amountCents: number, balanceCents: number): PaymentCheck {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return { ok: false, reason: 'Payment amount must be a positive number of cents' };
  if (balanceCents <= 0) return { ok: false, reason: 'This invoice has no open balance' };
  if (amountCents > balanceCents) return { ok: false, reason: 'Payment exceeds the open balance' };
  return { ok: true };
}

/** Retainage still held across a project's invoices: withheld on billings minus released. */
export function retainageHeld(invoices: { kind: string; status: InvoiceStatus; retainageCents: number; subtotalCents: number }[]): number {
  let held = 0;
  for (const inv of invoices) {
    if (inv.status === 'void' || inv.status === 'draft') continue;
    if (inv.kind === 'retainage') held -= inv.subtotalCents;
    else held += inv.retainageCents;
  }
  return held;
}
